"use client";

import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { usePathname } from "@/i18n/navigation";
import Image from "next/image";
import { Menu, X } from "lucide-react";
import { useState } from "react";
import { COURSES_PUBLIC_ENABLED } from "@/lib/feature-flags";
import type { CtfBadge } from "@/lib/website/ctf-badge";

export function Navbar({ locale, ctfBadge = null }: { locale: string; ctfBadge?: CtfBadge }) {
  const t = useTranslations("website.nav");
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  // The CTF link shows a live badge: "Open" (gold) when an event is running,
  // "New" (accent) when one is upcoming, nothing when there is none.
  const ctfBadgeLabel = ctfBadge ? t(ctfBadge === "open" ? "ctfBadgeOpen" : "ctfBadgeNew") : null;
  const NavLabel = ({ href, label }: { href: string; label: string }) =>
    href === "/ctf" && ctfBadgeLabel ? (
      <span className="inline-flex items-center gap-1.5">
        {label}
        <span
          className="rounded-full px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none"
          style={
            ctfBadge === "open"
              ? { background: "linear-gradient(135deg, #C9A535, #E8C84A)", color: "#0A1628" }
              : { background: "var(--fm-accent)", color: "var(--fm-background)" }
          }
        >
          {ctfBadgeLabel}
        </span>
      </span>
    ) : (
      <>{label}</>
    );

  const links: { href: string; label: string }[] = [
    { href: "/", label: t("home") },
    ...(COURSES_PUBLIC_ENABLED ? [
      { href: "/courses", label: t("courses") },
      { href: "/mentors", label: t("instructors") },
    ] : []),
    { href: "/ctf", label: t("ctf") },
    { href: "/blog", label: t("blog") },
    { href: "/about", label: t("about") },
    { href: `/${locale}#contact`, label: t("contact") },
  ];

  // Same-page hash links must render as native <a> — next-intl's Link
  // re-prefixes the locale on every click, which compounds the hash
  // ("/en#contact" → "/en#contact#contact" on the second click).
  const isHash = (href: string) => href.includes("#");
  const isActive = (href: string) =>
    pathname === href || (href !== "/" && pathname.startsWith(`${href}/`));
  // Locale-prefixed on purpose. A bare "/#contact" hits next-intl's middleware,
  // which re-resolves the locale from the NEXT_LOCALE cookie or Accept-Language
  // — so an Arabic reader on an English-preferring browser lands on /en#contact
  // from the site's primary CTA. It also costs an extra redirect hop.
  const contactHref = `/${locale}#contact`;

  const otherLocale = locale === "en" ? "ar" : "en";

  return (
    <header className="sticky top-0 z-40 border-b border-(--fm-border) bg-(--fm-background)/80 backdrop-blur-md">
      <nav className="max-w-7xl mx-auto px-6 h-32 flex items-center justify-between">
        {/* Logo */}
        <Link
          href="/"
          className="flex items-center gap-3"
          aria-label={locale === "ar" ? "الرئيسية - فورت مايند" : "FortMind Home"}
        >
          <Image src="/logo.png" alt="FortMind" width={96} height={96} className="shrink-0" priority />
          <div className="flex flex-col">
            <span className="font-heading text-3xl font-bold tracking-widest uppercase leading-tight">
              <span className="text-(--fm-text-primary)">{t("fort")}</span>
              <span className="text-(--fm-accent)">{t("mind")}</span>
            </span>
            <span className="text-2xs text-(--fm-text-secondary) tracking-wider uppercase leading-tight">
              {t("tagline")}
            </span>
          </div>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-5 xl:gap-8">
          {links.map((link) => {
            const className = `text-base xl:text-lg whitespace-nowrap transition-colors ${isActive(link.href)
              ? "text-(--fm-accent) font-medium"
              : "text-(--fm-text-secondary) hover:text-(--fm-text-primary)"
              }`;
            return isHash(link.href) ? (
              <a key={link.href} href={link.href} className={className}>
                <NavLabel href={link.href} label={link.label} />
              </a>
            ) : (
              <Link key={link.href} href={link.href} className={className}>
                <NavLabel href={link.href} label={link.label} />
              </Link>
            );
          })}
        </div>

        {/* Right side */}
        <div className="hidden lg:flex items-center gap-4">
          <Link
            href={pathname}
            locale={otherLocale}
            className="text-md whitespace-nowrap text-(--fm-text-secondary) hover:text-(--fm-text-primary) transition-colors"
            aria-label={locale === "en" ? "التبديل إلى العربية" : "Switch to English"}
          >
            {locale === "en" ? "العربية" : "English"}
          </Link>
          {/* Native <a> on purpose: next-intl Link re-prefixes the locale on
              every click, which compounds the hash to /en#contact#contact on
              repeat taps. */}
          <a
            href={contactHref}
            className="px-4 py-2 text-sm whitespace-nowrap rounded-lg bg-(--fm-accent) text-(--fm-background) font-medium hover:bg-(--fm-accent-hover) transition-colors"
          >
            {t("getStarted")}
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden p-2 text-(--fm-text-primary)"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label={
            mobileOpen
              ? locale === "ar"
                ? "إغلاق القائمة"
                : "Close menu"
              : locale === "ar"
                ? "فتح القائمة"
                : "Open menu"
          }
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-(--fm-border) bg-(--fm-background) px-6 py-4 space-y-4">
          {links.map((link) => {
            const className = `block text-sm ${isActive(link.href)
              ? "text-(--fm-accent) font-medium"
              : "text-(--fm-text-secondary)"
              }`;
            return isHash(link.href) ? (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={className}
              >
                <NavLabel href={link.href} label={link.label} />
              </a>
            ) : (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                className={className}
              >
                <NavLabel href={link.href} label={link.label} />
              </Link>
            );
          })}
          <div className="flex items-center gap-4 pt-4 border-t border-(--fm-border)">
            <Link
              href={pathname}
              locale={otherLocale}
              className="text-sm text-(--fm-text-secondary)"
              aria-label={locale === "en" ? "التبديل إلى العربية" : "Switch to English"}
            >
              {locale === "en" ? "العربية" : "English"}
            </Link>
            <a
              href={contactHref}
              onClick={() => setMobileOpen(false)}
              className="px-4 py-2 text-sm whitespace-nowrap rounded-lg bg-(--fm-accent) text-(--fm-background) font-medium"
            >
              {t("getStarted")}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
